import BaseAPI from './base.api';
import {
  BulkUpdateRawResponse,
  BulkUpdateRequest,
  BulkUpdateResponse,
  CtxDataFile,
  FileAction,
} from 'interface/file/file.interface';
import { PaginationResponse } from 'interface/shared/api.interface';
import axios from 'axios';

const BULK_UPDATE_MAX_FILES = 1000;

export default class FileAPI extends BaseAPI {
  private ROUTES = {
    allCtxDataFiles: '/context/ctxdataimport/files',
    bulkUpdateFiles: '/context/ctxdataimport/files',
    markAsUploaded: '/kernel/file-manager/files/:id/uploaded',
    preSigned: '/kernel/file-manager/presigned',
    processCtxFile: '/context/ctxdataimport/files/import',
    updateFile: '/context/ctxdataimport/files/:id',
    uploadCtxFile: '/kernel/file-manager/files/upload',
  };

  params = {
    limit: 0,
    offset: 0,
    search: '',
    sortby: 'createdAt',
    sortorder: 'desc',
  };

  getAllCtxDataFiles(opts = {}): Promise<PaginationResponse<CtxDataFile>> {
    this.params = Object.assign(this.params, opts);
    return this.GET(this.ROUTES.allCtxDataFiles, { ...this.params, fileType: 'context-data' }).then((res) => {
      if (res?.data && Array.isArray(res.data)) {
        return res;
      }

      return {
        data: [],
        pagination: {
          totalRecords: 0,
          limit: 1,
          offset: 0,
        },
      };
    });
  }

  uploadCtxFile(file: File, uuidIdentifier: string): Promise<CtxDataFile[]> {
    const fm = new FormData();
    fm.append('file', file);
    fm.append('isPrivate', 'true');
    fm.append('extension', '.csv');
    fm.append('tags', JSON.stringify(['context-data', uuidIdentifier]));

    return this.POST(this.ROUTES.uploadCtxFile, fm, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 30 * 1000,
    });
  }

  /**
   * @deprecated Use `processFile(fileId)` (PATCH /files/:id with action=PROCESS) instead.
   * The backend endpoint POST /context/ctxdataimport/files/import is deprecated and
   * internally maps to the unified PATCH state-management API.
   */
  processFileById(fileId: string): Promise<CtxDataFile> {
    return this.POST(this.ROUTES.processCtxFile, { fileId });
  }

  updateFile(fileId: string, action: FileAction): Promise<CtxDataFile> {
    return this.PATCH(this.buildURL(this.ROUTES.updateFile, { id: fileId }), { action });
  }

  processFile(fileId: string): Promise<CtxDataFile> {
    return this.updateFile(fileId, 'PROCESS');
  }

  pauseFile(fileId: string): Promise<CtxDataFile> {
    return this.updateFile(fileId, 'PAUSE');
  }

  resumeFile(fileId: string): Promise<CtxDataFile> {
    return this.updateFile(fileId, 'RESUME');
  }

  applyFileAction(action: FileAction, fileIds: string[]): Promise<BulkUpdateResponse> {
    if (fileIds.length === 0) {
      return Promise.reject(new Error('No files provided.'));
    }
    if (fileIds.length === 1) {
      const fileId = fileIds[0];
      return this.updateFile(fileId, action)
        .then(
          (file): BulkUpdateResponse => ({
            action,
            status: 'ALL_SUCCESS',
            summary: { requested: 1, succeeded: 1, failed: 0 },
            results: [{ fileId: file.id, fileStatus: file.status, status: 'SUCCESS', statusMessage: '' }],
          }),
        )
        .catch(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (err: any): BulkUpdateResponse => ({
            action,
            status: 'ALL_FAILED',
            summary: { requested: 1, succeeded: 0, failed: 1 },
            results: [
              {
                fileId,
                fileStatus: 'PROCESSING_FAILED',
                status: 'ERROR',
                statusMessage: err?.response?.data?.error?.exceptionMessage ?? 'Action failed.',
              },
            ],
          }),
        );
    }
    return this.bulkUpdateFiles({ action, fileIds });
  }

  bulkUpdateFiles(payload: BulkUpdateRequest): Promise<BulkUpdateResponse> {
    const { action, fileIds, tags } = payload;

    if ((!fileIds || fileIds.length === 0) && (!tags || tags.length === 0)) {
      return Promise.reject(new Error('Either fileIds or tags must be provided.'));
    }

    const dedupedFileIds = fileIds && fileIds.length > 0 ? Array.from(new Set(fileIds)) : undefined;

    if (dedupedFileIds && dedupedFileIds.length > BULK_UPDATE_MAX_FILES) {
      return Promise.reject(new Error(`Bulk update exceeds max batch size of ${BULK_UPDATE_MAX_FILES} files.`));
    }

    const body: BulkUpdateRequest = { action };
    if (dedupedFileIds) {
      body.fileIds = dedupedFileIds;
    }
    if (tags && tags.length > 0) {
      body.tags = tags;
    }

    return this.PATCH(this.ROUTES.bulkUpdateFiles, body).then((raw: BulkUpdateRawResponse) =>
      this.normalizeBulkResponse(raw, action, dedupedFileIds ?? []),
    );
  }

  // The backend reports one collective new file-status + the touched fileIds.
  // We synthesize the runner-friendly shape so error / partial paths exist for
  // future expansion without callers branching on the wire format.
  private normalizeBulkResponse(
    raw: BulkUpdateRawResponse,
    action: FileAction,
    requestedFileIds: string[],
  ): BulkUpdateResponse {
    const updatedIds = raw?.fileIds ?? [];
    const updatedSet = new Set(updatedIds);
    const successfulIds =
      requestedFileIds.length > 0 ? requestedFileIds.filter((id) => updatedSet.has(id)) : updatedIds;
    const failedIds = requestedFileIds.length > 0 ? requestedFileIds.filter((id) => !updatedSet.has(id)) : [];

    const succeeded = successfulIds.length;
    const requested = requestedFileIds.length || succeeded;
    const failed = failedIds.length || Math.max(0, requested - succeeded);
    const fileStatus = raw?.status;
    const statusMessage = raw?.statusMessage ?? '';

    return {
      action,
      status: failed === 0 ? 'ALL_SUCCESS' : succeeded === 0 ? 'ALL_FAILED' : 'PARTIAL_SUCCESS',
      summary: { requested, succeeded, failed },
      results: [
        ...successfulIds.map((fileId) => ({
          fileId,
          fileStatus,
          status: 'SUCCESS' as const,
          statusMessage,
        })),
        ...failedIds.map((fileId) => ({
          fileId,
          fileStatus,
          status: 'ERROR' as const,
          statusMessage: statusMessage || 'Not acknowledged by backend.',
        })),
      ],
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async uploadViaPreSignedUrl(
    file: File,
    setFileUploadedPercentage: (percentCompleted: number) => void,
    journeyGUID: string,
  ): Promise<any> {
    const preSignedMetadataRequest = await this.POST(this.ROUTES.preSigned, {
      friendlyName: file.name,
      tags: ['context-data', `${journeyGUID}`],
      size: file.size,
    }).catch((axiosError) => axiosError);

    if (!preSignedMetadataRequest.url || !preSignedMetadataRequest.fileId) {
      let message = 'The configuration required to upload the file could not be retrieved.';

      if (preSignedMetadataRequest.response?.data?.error?.exceptionMessage) {
        message = preSignedMetadataRequest.response.data.error.exceptionMessage;
      }

      throw { message, axiosError: preSignedMetadataRequest };
    }

    const s3Request = await axios
      .put(preSignedMetadataRequest.url, file, {
        headers: {
          'Content-Type': 'text/csv',
          'If-None-Match': '*',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setFileUploadedPercentage(percentCompleted);
          }
        },
      })
      .catch((axiosError) => axiosError);

    if (!s3Request.status) {
      let message = 'The file could not be uploaded to the S3 bucket.';

      if (s3Request.response?.data?.error?.exceptionMessage) {
        message = s3Request.response.data.error.exceptionMessage;
      } else if (s3Request.message) {
        message = s3Request.message;
      }

      throw { message, axiosError: s3Request };
    }

    const uploadedRequest = await this.PUT(
      this.buildURL(this.ROUTES.markAsUploaded, { id: preSignedMetadataRequest.fileId }),
    ).catch((axiosError) => axiosError);

    if (!uploadedRequest.id) {
      let message = 'The file could not be marked as uploaded.';

      if (uploadedRequest.response?.data?.error?.exceptionMessage) {
        message = uploadedRequest.response.data.error.exceptionMessage;
      } else if (uploadedRequest.message) {
        message = uploadedRequest.message;
      }

      throw { message, axiosError: uploadedRequest };
    }

    return {
      fileId: preSignedMetadataRequest.fileId,
      response: uploadedRequest,
    };
  }
}
